import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import "./GlassDateTimePicker.scss";

type TimeField = "hour" | "minute";

interface GlassDateTimePickerProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  disabled?: boolean;
}

interface MenuAnchor {
  openUp: boolean;
}

const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"] as const;

const MONTHS = [
  "Январь",
  "Февраль",
  "Март",
  "Апрель",
  "Май",
  "Июнь",
  "Июль",
  "Август",
  "Сентябрь",
  "Октябрь",
  "Ноябрь",
  "Декабрь",
] as const;

const pad = (value: number) => String(value).padStart(2, "0");

const parseLocalDateTime = (value: string): Date | null => {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;

  const [, year, month, day, hour, minute] = match;
  const date = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute)
  );
  return Number.isNaN(date.getTime()) ? null : date;
};

const toLocalDateTimeValue = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;

const formatDisplay = (value: string): string => {
  const date = parseLocalDateTime(value);
  if (!date) return "Выберите дату и время";

  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const startOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);

const buildCalendarDays = (month: Date): Array<Date | null> => {
  const first = startOfMonth(month);
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const mondayIndex = (first.getDay() + 6) % 7;
  const cells: Array<Date | null> = Array.from({ length: mondayIndex }, () => null);

  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(new Date(month.getFullYear(), month.getMonth(), day));
  }

  while (cells.length % 7 !== 0) {
    cells.push(null);
  }

  return cells;
};

const HOURS = Array.from({ length: 24 }, (_, index) => pad(index));
const MINUTES = Array.from({ length: 60 }, (_, index) => pad(index));

const sameDay = (left: Date, right: Date) =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

export const GlassDateTimePicker = ({
  id,
  value,
  onChange,
  className,
  disabled = false,
}: GlassDateTimePickerProps) => {
  const selected = useMemo(() => parseLocalDateTime(value), [value]);
  const [open, setOpen] = useState(false);
  const [openTimeField, setOpenTimeField] = useState<TimeField | null>(null);
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(selected ?? new Date()));
  const [draftHour, setDraftHour] = useState(() => pad(selected?.getHours() ?? 12));
  const [draftMinute, setDraftMinute] = useState(() => pad(selected?.getMinutes() ?? 0));

  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const hourListRef = useRef<HTMLDivElement>(null);
  const minuteListRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<MenuAnchor>({ openUp: false });
  const syncFrameRef = useRef(0);

  useEffect(() => {
    if (disabled && open) {
      setOpen(false);
      setOpenTimeField(null);
    }
  }, [disabled, open]);

  useEffect(() => {
    if (!open) {
      setOpenTimeField(null);
      return;
    }
    setViewMonth(startOfMonth(selected ?? new Date()));
    setDraftHour(pad(selected?.getHours() ?? 12));
    setDraftMinute(pad(selected?.getMinutes() ?? 0));
  }, [open, selected]);

  useLayoutEffect(() => {
    if (!openTimeField) return;
    const list = openTimeField === "hour" ? hourListRef.current : minuteListRef.current;
    const active = list?.querySelector<HTMLElement>(".is-active");
    active?.scrollIntoView({ block: "center" });
  }, [openTimeField]);

  const initMenuAnchor = useCallback(() => {
    if (!rootRef.current) return;

    const rect = rootRef.current.getBoundingClientRect();
    const gap = 8;
    const estimatedHeight = 320;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openUp = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;

    anchorRef.current = {
      openUp,
    };
  }, []);

  const syncMenuPosition = useCallback(() => {
    if (!rootRef.current || !menuRef.current) return;

    const rect = rootRef.current.getBoundingClientRect();
    const gap = 8;
    const menu = menuRef.current;
    const { openUp } = anchorRef.current;
    const width = Math.min(Math.max(rect.width, 300), 320);

    menu.style.position = "fixed";
    menu.style.left = `${Math.min(rect.left, window.innerWidth - width - 12)}px`;
    menu.style.width = `${width}px`;
    menu.style.maxHeight = "none";
    menu.style.zIndex = "1400";

    if (openUp) {
      menu.style.top = "auto";
      menu.style.bottom = `${window.innerHeight - rect.top + gap}px`;
    } else {
      menu.style.bottom = "auto";
      menu.style.top = `${rect.bottom + gap}px`;
    }

    menu.style.visibility = "visible";
  }, []);

  const scheduleMenuPositionSync = useCallback(() => {
    if (syncFrameRef.current) return;
    syncFrameRef.current = window.requestAnimationFrame(() => {
      syncFrameRef.current = 0;
      syncMenuPosition();
    });
  }, [syncMenuPosition]);

  useLayoutEffect(() => {
    if (!open) return;

    initMenuAnchor();
    if (menuRef.current) {
      menuRef.current.style.visibility = "hidden";
    }
    syncMenuPosition();

    const onScrollOrResize = () => scheduleMenuPositionSync();
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);

    return () => {
      if (syncFrameRef.current) {
        window.cancelAnimationFrame(syncFrameRef.current);
        syncFrameRef.current = 0;
      }
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [open, initMenuAnchor, syncMenuPosition, scheduleMenuPositionSync]);

  useEffect(() => {
    if (!open) return;

    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };

    const onEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onEscape);

    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onEscape);
    };
  }, [open]);

  const calendarDays = useMemo(() => buildCalendarDays(viewMonth), [viewMonth]);
  const today = useMemo(() => new Date(), []);

  const applyDate = useCallback(
    (day: Date) => {
      const next = new Date(
        day.getFullYear(),
        day.getMonth(),
        day.getDate(),
        Number(draftHour),
        Number(draftMinute)
      );
      onChange(toLocalDateTimeValue(next));
    },
    [draftHour, draftMinute, onChange]
  );

  const applyTime = useCallback(
    (hour: string, minute: string) => {
      setDraftHour(hour);
      setDraftMinute(minute);
      const base = selected ?? new Date();
      const next = new Date(
        base.getFullYear(),
        base.getMonth(),
        base.getDate(),
        Number(hour),
        Number(minute)
      );
      onChange(toLocalDateTimeValue(next));
    },
    [onChange, selected]
  );

  const hasValue = Boolean(selected);

  return (
    <div
      className={`glass-datetime${open ? " is-open" : ""}${disabled ? " is-disabled" : ""}${
        className ? ` ${className}` : ""
      }`}
      ref={rootRef}
    >
      <button
        id={id}
        type="button"
        className={`glass-datetime-trigger${hasValue ? "" : " is-placeholder"}`}
        onClick={() => {
          if (disabled) return;
          setOpen((prev) => !prev);
        }}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span>{formatDisplay(value)}</span>
        <CalendarDays size={16} className="glass-datetime-trigger-icon" />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="glass-datetime-menu"
            style={{ visibility: "hidden" }}
            role="dialog"
            aria-label="Выбор даты и времени"
          >
            <div className="glass-datetime-month">
              <button
                type="button"
                className="glass-datetime-nav"
                onClick={() =>
                  setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))
                }
                aria-label="Предыдущий месяц"
              >
                <ChevronLeft size={16} />
              </button>
              <div className="glass-datetime-month-label">
                {MONTHS[viewMonth.getMonth()]} {viewMonth.getFullYear()}
              </div>
              <button
                type="button"
                className="glass-datetime-nav"
                onClick={() =>
                  setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))
                }
                aria-label="Следующий месяц"
              >
                <ChevronRight size={16} />
              </button>
            </div>

            <div className="glass-datetime-weekdays">
              {WEEKDAYS.map((day) => (
                <span key={day}>{day}</span>
              ))}
            </div>

            <div className="glass-datetime-days">
              {calendarDays.map((day, index) => {
                if (!day) {
                  return <span key={`empty-${index}`} className="glass-datetime-day is-empty" />;
                }

                const isSelected = selected ? sameDay(day, selected) : false;
                const isToday = sameDay(day, today);

                return (
                  <button
                    key={day.toISOString()}
                    type="button"
                    className={`glass-datetime-day${isSelected ? " is-selected" : ""}${
                      isToday ? " is-today" : ""
                    }`}
                    onClick={() => {
                      setOpenTimeField(null);
                      applyDate(day);
                    }}
                  >
                    {day.getDate()}
                  </button>
                );
              })}
            </div>

            <div className="glass-datetime-time">
              <span className="glass-datetime-time-label">Время</span>
              <div className="glass-datetime-time-controls">
                <div
                  className={`glass-datetime-time-field${
                    openTimeField === "hour" ? " is-open" : ""
                  }`}
                >
                  <button
                    type="button"
                    className="glass-datetime-time-trigger"
                    aria-label="Часы"
                    aria-haspopup="listbox"
                    aria-expanded={openTimeField === "hour"}
                    onClick={() =>
                      setOpenTimeField((current) => (current === "hour" ? null : "hour"))
                    }
                  >
                    <span>{draftHour}</span>
                    <ChevronDown size={14} className="glass-datetime-time-chevron" />
                  </button>
                  {openTimeField === "hour" && (
                    <div
                      ref={hourListRef}
                      className="glass-datetime-time-list"
                      role="listbox"
                      aria-label="Часы"
                    >
                      {HOURS.map((hour) => (
                        <button
                          key={hour}
                          type="button"
                          role="option"
                          aria-selected={hour === draftHour}
                          className={`glass-datetime-time-option${
                            hour === draftHour ? " is-active" : ""
                          }`}
                          onClick={() => {
                            applyTime(hour, draftMinute);
                            setOpenTimeField(null);
                          }}
                        >
                          {hour}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <span className="glass-datetime-time-sep">:</span>
                <div
                  className={`glass-datetime-time-field${
                    openTimeField === "minute" ? " is-open" : ""
                  }`}
                >
                  <button
                    type="button"
                    className="glass-datetime-time-trigger"
                    aria-label="Минуты"
                    aria-haspopup="listbox"
                    aria-expanded={openTimeField === "minute"}
                    onClick={() =>
                      setOpenTimeField((current) => (current === "minute" ? null : "minute"))
                    }
                  >
                    <span>{draftMinute}</span>
                    <ChevronDown size={14} className="glass-datetime-time-chevron" />
                  </button>
                  {openTimeField === "minute" && (
                    <div
                      ref={minuteListRef}
                      className="glass-datetime-time-list"
                      role="listbox"
                      aria-label="Минуты"
                    >
                      {MINUTES.map((minute) => (
                        <button
                          key={minute}
                          type="button"
                          role="option"
                          aria-selected={minute === draftMinute}
                          className={`glass-datetime-time-option${
                            minute === draftMinute ? " is-active" : ""
                          }`}
                          onClick={() => {
                            applyTime(draftHour, minute);
                            setOpenTimeField(null);
                          }}
                        >
                          {minute}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="glass-datetime-footer">
              <button
                type="button"
                className="glass-datetime-clear"
                onClick={() => {
                  onChange("");
                  setOpen(false);
                }}
              >
                Очистить
              </button>
              <button type="button" className="glass-datetime-done" onClick={() => setOpen(false)}>
                Готово
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};
